import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { usePersonalDetails } from "app/hooks/applicant/usePersonalDetails";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/jointpersonalDetails";
import { RootState } from "app/lib/store/store";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InfoTitle from "app/components/common/typography/InfoTitle";

import toast from "react-hot-toast";
import { JointPersonalFormThreeData } from "app/utils/types";
import { APPLICANT } from "app/constants/apiEndPoints";
import DynamicFormThree from "app/components/Individual/personalDetails/dynamicForms/DynamicFormThree";

const PartThree: React.FC = () => {
  const dispatch = useDispatch();

  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );
  const { mutateAsync: submitPersonalDetails, isPending } =
    usePersonalDetails();
  const { formThree, ...others } = useSelector(
    (state: RootState) => state.jointpersonalDetails.stepsData
  );
  const applicant = useSelector(
    (state: RootState) => state.loanTypesSteps.jointApplication
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<JointPersonalFormThreeData>();

  const onSubmit = async (data: JointPersonalFormThreeData) => {
    const { yourDetails, coBorrowerDetails } = data || {};
    if (!savedIds?.secondApplicantID) {
      toast.error("Applicant ID not found!");
      return;
    }
    const formatPhone = (value: string | undefined) =>
      value?.trim() ? (value.startsWith("+") ? value : `+${value}`) : "";

    const formattedYourDetails = {
      ...yourDetails,
      phoneNumber: formatPhone(yourDetails?.phoneNumber),
      email: yourDetails.email?.toLowerCase(),
    };
    const formattedCoBorrowerDetails = {
      ...coBorrowerDetails,
      phoneNumber: formatPhone(coBorrowerDetails?.phoneNumber),
      email: coBorrowerDetails.email?.toLowerCase(),
    };
    const request1 = {
      url: `${APPLICANT}/${savedIds?.applicantID}/third`,
      data: formattedYourDetails,
    };
    const request2 = {
      url: `${APPLICANT}/${savedIds?.secondApplicantID}/third`,
      data: formattedCoBorrowerDetails,
    };
    const storedData = {
      yourDetails: { ...yourDetails },
      coBorrowerDetails: { ...coBorrowerDetails },
    };

    try {
      const [response1, response2] = await Promise.all([
        submitPersonalDetails(request1),
        submitPersonalDetails(request2),
      ]);
      if (response1?.success && response2?.success) {
        dispatch(nextStep());
        dispatch(
          addStepsData({
            ...others,
            formThree: storedData,
          })
        );
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong.");
    }
  };

  useEffect(() => {
    if (formThree) {
      const cleanedData = {
        ...formThree,
        yourDetails: {
          ...formThree.yourDetails,
          phoneNumber:
            formThree.yourDetails?.phoneNumber?.replace(/^\+/, "") || "",
          landline: formThree.yourDetails?.landline?.replace(/^\+/, "") || "",
          workNumber:
            formThree.yourDetails?.workNumber?.replace(/^\+/, "") || "",
        },
        coBorrowerDetails: {
          ...formThree.coBorrowerDetails,
          phoneNumber:
            formThree.coBorrowerDetails?.phoneNumber?.replace(/^\+/, "") || "",
          landline:
            formThree.coBorrowerDetails?.landline?.replace(/^\+/, "") || "",
          workNumber:
            formThree.coBorrowerDetails?.workNumber?.replace(/^\+/, "") || "",
        },
      };

      reset(cleanedData);
    } else {
      const { YourDetails, Coborrower } = applicant || {};
      if (YourDetails?.phoneNumber) {
        setValue(
          "yourDetails.phoneNumber",
          (YourDetails.phoneNumber || "").replace(/^\+/, "")
        );
      }
      if (Coborrower?.phoneNumber) {
        setValue("coBorrowerDetails.phoneNumber", Coborrower.phoneNumber);
        setValue(
          "coBorrowerDetails.phoneNumber",
          (Coborrower.phoneNumber || "").replace(/^\+/, "")
        );
      }
      if (YourDetails?.email) {
        setValue("yourDetails.email", YourDetails.email);
      }
      if (Coborrower?.email) {
        setValue("coBorrowerDetails.email", Coborrower.email);
      }
    }
  }, [formThree, reset]);

  return (
    <>
      <ProgressBar progress={50} MB={4} />
      <div className="w-full sm:w-[60%] lg:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Personal Details" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-5 flex-col md:flex-col xl:flex-row sm:gap-4 ">
            <div className="flex-1">
              <InfoTitle title="Your Details" />

              <DynamicFormThree
                control={control}
                errors={errors}
                phoneNumber="yourDetails.phoneNumber"
                landline="yourDetails.landline"
                workNumber="yourDetails.workNumber"
                email="yourDetails.email"
                preferredMethodOfContact="yourDetails.preferredMethodOfContact"
                disabledEmailPhone={true}
              />
            </div>
            <div className="flex-1">
              <InfoTitle title="Co-borrower" />

              <DynamicFormThree
                control={control}
                errors={errors}
                phoneNumber="coBorrowerDetails.phoneNumber"
                landline="coBorrowerDetails.landline"
                workNumber="coBorrowerDetails.workNumber"
                email="coBorrowerDetails.email"
                preferredMethodOfContact="coBorrowerDetails.preferredMethodOfContact"
                disabledEmail={true}
              />
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom
              onClick={() => {
                dispatch(prevStep());
              }}
            >
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartThree;
